import type {
  ComercialOverview as ComercialPayload,
  Links,
} from '@ops/contracts'
import { formatPeriodo } from '@ops/contracts'

import { irA } from '../../lib/scroll'
import { formatMetricValue, formatRelativeDays, plural } from '../../lib/format'
import {
  Button,
  Card,
  HeroKpi,
  KpiCard,
  Section,
  Stack,
  StatusPill,
  TriageBar,
  TwoCol,
} from '../../ui'
import { CanalesPanel } from './CanalesPanel'
import { ClientesPanel } from './ClientesPanel'
import { DeudaDatosCard } from './DeudaDatosCard'
import {
  baseDe,
  baseDelFrente,
  metricaDelFrente,
  porSlug,
} from './derivar'
import { FollowupsRail } from './FollowupsRail'
import { FrenteCard, FrentesLista } from './FrenteCard'
import { FunnelPanel } from './FunnelPanel'
import { Vacio } from '../comunes'
import {
  claveEntidad,
  frentesDeAlertas,
  indiceCobertura,
  metricasEnMovimiento,
  metricasSanas,
  ultimaCarga,
} from './frentes'
import { deudaDeDatos } from './deuda'
import {
  canalesSinLectura,
  cuelloDelFunnel,
  titularComercial,
} from './narrativa'
import type { Tablero } from './tablero'
import { Titular } from './Titular'
import { tableroDeTriage } from './triage'
import { ZonaSana } from './ZonaSana'

/**
 * Overview comercial — "¿cómo viene la máquina de adquisición?".
 *
 * Los frentes son el cuello del funnel y los canales sin lectura. **Sin un solo
 * dato de $$**: no hay MRR global, ni valor estimado de oportunidad, ni costo de
 * canal. El servidor los recorta y la pantalla no dibuja la columna.
 */
export interface ComercialOverviewProps {
  payload: ComercialPayload & { links: Links }
  tablero: Tablero
}

const ZONA_1 = 'zona-1'
const DEUDA = 'deuda-datos'

export function ComercialOverviewVista({
  payload,
  tablero,
}: ComercialOverviewProps) {
  const { now, meta } = tablero

  const leads = porSlug(payload.heroMetrics, 'leads_mes')
  const cierres = porSlug(payload.heroMetrics, 'cierres_ganados')
  const resto = payload.heroMetrics.filter(
    (m) => m.slug !== 'leads_mes' && m.slug !== 'cierres_ganados',
  )

  const criticos = frentesDeAlertas(payload.alerts, 'critical')
  // Misma corrección que en los otros dos Overview: la deuda sale de las
  // alertas del payload, que cubren todo el universo de métricas del rol, y no
  // solo de las cuatro de la fila héroe.
  const deuda = deudaDeDatos(payload.alerts, payload.heroMetrics, meta.valorDe)
  const faltantes = deuda.sinDato
  const viejas = deuda.vencidas
  const sanas = metricasSanas(resto)
  const moviendose = metricasEnMovimiento(resto)

  const cobertura = indiceCobertura(payload.misFollowups, payload.links)
  const cuello = cuelloDelFunnel(payload.funnel)
  const sinLectura = canalesSinLectura(payload.canales)
  const cargado = ultimaCarga(payload.heroMetrics)

  const triage = tableroDeTriage(
    [
      {
        severidad: 'critical',
        cantidad: cuello === null ? 0 : 1,
        label: 'cuellos en el funnel',
        labelSingular: 'cuello en el funnel',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'critical',
        cantidad: criticos.length,
        label: 'frentes rotos',
        labelSingular: 'frente roto',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'warning',
        cantidad: sinLectura.length,
        label: 'canales sin lectura',
        labelSingular: 'canal sin lectura',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'unknown',
        cantidad: faltantes.length,
        label: 'números sin dato',
        labelSingular: 'número sin dato',
        onClick: () => irA(DEUDA),
      },
      {
        severidad: 'ok',
        cantidad: sanas.length,
        label: 'frentes en target',
        labelSingular: 'frente en target',
      },
    ],
    'El funnel no tiene cuellos y ningún canal quedó a ciegas',
  )

  return (
    <Stack gap="lg">
      <Titular
        titular={titularComercial(payload, deuda, now)}
        periodo={formatPeriodo(payload.periodo)}
      />

      {leads === null ? (
        <Vacio icono={null}
          mensaje="El tablero comercial no tiene todavía su métrica de entrada"
          detalle="Las métricas de la fila héroe se definen en el catálogo, en Admin."
          punteada
        />
      ) : (
        <HeroKpi
          metric={leads}
          secundaria={cierres}
          base={baseDe(leads)}
          direccion={meta.direccionDe(leads.slug)}
          aside={
            <FunnelPanel
              funnel={payload.funnel}
              titulo="El funnel del mes, etapa por etapa"
            />
          }
          onLoadData={() => tablero.cargarDato(leads)}
          now={now}
          acciones={
            <Button
              variante="secundario"
              onClick={() => tablero.verDetalle(leads)}
            >
              Ver definición y umbrales
            </Button>
          }
        />
      )}

      <TriageBar
        estado={triage.estado}
        titular={triage.titular}
        conteos={triage.conteos}
        meta={
          cargado === null
            ? 'ninguna métrica tiene carga registrada'
            : `carga más reciente: ${formatRelativeDays(cargado, now)}`
        }
        acciones={
          <>
            <Button variante="primario" onClick={() => irA(ZONA_1)}>
              Atacar el cuello de botella
              {cuello === null
                ? ''
                : ` (${cuello.desde.toLowerCase()} → ${cuello.hasta.toLowerCase()})`}
            </Button>
            <Button variante="secundario" onClick={() => irA(DEUDA)}>
              Cargar los datos que faltan (
              {formatMetricValue(faltantes.length + viejas.length, 'count')})
            </Button>
          </>
        }
        className="rounded-card border border-border"
      />

      <Section
        id={ZONA_1}
        kicker="Zona 1"
        titulo="Lo que está roto"
        bajada={
          <>
            {cuello === null ? (
              'Ninguna etapa del funnel tiene conversión medida contra la anterior, así que el cuello todavía no se puede señalar.'
            ) : (
              <>
                El funnel se cae de {cuello.desde.toLowerCase()} a{' '}
                {cuello.hasta.toLowerCase()}: pasa el{' '}
                <b>{formatMetricValue(cuello.conversionPct, 'percent')}</b>.
              </>
            )}{' '}
            {sinLectura.length > 0 ? (
              <>
                Además <b>{sinLectura.length}</b> de{' '}
                <b>{payload.canales.length}</b>{' '}
                {plural(sinLectura.length, 'canal', 'canales')} no{' '}
                {plural(sinLectura.length, 'está activo', 'están activos')} y no{' '}
                {plural(sinLectura.length, 'aporta', 'aportan')} lectura de
                conversión.
              </>
            ) : (
              'Todos los canales están activos y aportan lectura.'
            )}
          </>
        }
      >
        <TwoCol
          ratio="rail"
          asideSticky
          aside={
            <FollowupsRail
              grupos={payload.misFollowups}
              links={payload.links}
              titulo="Mis seguimientos"
              vacio="No tenés seguimientos abiertos. Asignate uno desde cualquier frente y se crea el work item en Plane."
            />
          }
        >
          <Stack gap="md">
            {cuello !== null ? (
              <Card severidad="critical" padding="md">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <StatusPill severidad="critical" caps size="sm">
                    Cuello del funnel
                  </StatusPill>
                  <h4 className="text-titulo font-medium text-text">
                    De {cuello.desde.toLowerCase()} a{' '}
                    {cuello.hasta.toLowerCase()}
                  </h4>
                </div>
                <p className="max-w-prosa text-meta text-muted-strong">
                  Es la peor conversión de{' '}
                  {formatPeriodo(payload.funnel.periodo)}: pasa el{' '}
                  {formatMetricValue(cuello.conversionPct, 'percent')}. La base de
                  ese porcentaje está en la tabla del funnel, al lado de cada
                  etapa.
                </p>
              </Card>
            ) : null}

            <FunnelPanel
              funnel={payload.funnel}
              titulo="De dónde a dónde se cae el funnel"
            />

            <CanalesPanel canales={payload.canales} />

            <FrentesLista
              frentes={criticos}
              vacio={
                <Vacio icono={null}
                  mensaje="Ninguna métrica comercial está fuera de umbral"
                  detalle="Los frentes de arriba son de funnel y de canales, no de métricas."
                />
              }
            >
              {(frente) => (
                <FrenteCard
                  key={frente.id}
                  frente={frente}
                  owner={
                    frente.entityType === 'metric'
                      ? meta.ownerDe(frente.entityId)
                      : null
                  }
                  cobertura={cobertura.get(
                    claveEntidad(frente.entityType, frente.entityId),
                  )}
                  onCrearSeguimiento={tablero.seguirFrente}
                  metric={metricaDelFrente(payload.heroMetrics, frente)}
                  base={baseDelFrente(payload.heroMetrics, frente)}
                  direccion={
                    frente.entityType === 'metric'
                      ? meta.direccionDe(frente.entityId)
                      : undefined
                  }
                  now={now}
                  onVerDetalle={tablero.verDetalle}
                />
              )}
            </FrentesLista>

            <DeudaDatosCard
              id={DEUDA}
              sinDato={faltantes}
              vencidas={viejas}
              onCargar={tablero.cargarDato}
              ownerDe={meta.ownerDe}
              now={now}
            />
          </Stack>
        </TwoCol>
      </Section>

      <Section
        kicker="Zona 2"
        titulo="Lo que se está moviendo"
        bajada={
          <>
            <b>{payload.oportunidadesExpansion.length}</b>{' '}
            {plural(
              payload.oportunidadesExpansion.length,
              'cuenta está en expansión',
              'cuentas están en expansión',
            )}
            : es la palanca que ya está adentro de la casa y no depende de traer
            un lead nuevo.
          </>
        }
      >
        <Stack gap="md">
          <ClientesPanel
            variante="expansion"
            clientes={payload.oportunidadesExpansion}
            links={payload.links}
            cobertura={cobertura}
            onCrearSeguimiento={tablero.seguirCliente}
            titulo="Cuentas con potencial de expansión"
            subtitulo="En estado expansión, de la más antigua a la más nueva"
            vacio="Ninguna cuenta está en expansión ahora mismo."
            footer="El valor de cada oportunidad vive en Close, no en el panel."
            sistema="close"
          />

          {moviendose.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {moviendose.map((metric) => (
                <KpiCard
                  key={metric.slug}
                  metric={metric}
                  base={baseDe(metric)}
                  direccion={meta.direccionDe(metric.slug)}
                  onLoadData={() => tablero.cargarDato(metric)}
                  onOpenDetail={() => tablero.verDetalle(metric)}
                  now={now}
                />
              ))}
            </div>
          ) : null}
        </Stack>
      </Section>

      <Section
        kicker="Zona 3"
        titulo="Lo que está sano"
        bajada="Colapsado: si la etapa o la métrica está en target, no pide atención hoy."
        regla={false}
        espaciado="sm"
      >
        <ZonaSana
          metricas={sanas}
          direccionDe={meta.direccionDe}
          onOpenDetail={tablero.verDetalle}
          now={now}
        />
      </Section>
    </Stack>
  )
}

